/**
 * EditServiceProviderConnectionDialog — first-class edit surface for a
 * service-provider connection stored under `serviceProviderConnections` in
 * `connections.json` (or the draft file in draft mode).
 *
 * Manifest-driven UI: reuses `ManifestConnectionForm` and
 * `useManifestFormState` so the auth / display-name affordances match
 * `EditConnectionDialog` (managed / ARM). The two dialogs share the form
 * body; the ingress + egress paths differ:
 *
 *   - Ingress: `connection` prop (already a `ServiceProviderConnection` from
 *     the local record) + fetched connector manifest via
 *     `useServiceProviderConnector` + `buildConnectorManifest`. No ARM
 *     round-trip.
 *   - Egress: on Save, run `extractServiceProviderAppSettings` to move any
 *     `AppConfiguration`-sourced leaves (secrets, connection strings) into
 *     app settings — `@appsetting('NAME')` references — before writing back.
 *     Then upsert app settings via `useUpsertEnvironmentSettings` and route
 *     the updated `ServiceProviderConnection` through the caller-supplied
 *     `onSave` (typically `handleSaveLocalConnection`), keeping draft/prod
 *     routing authoritative.
 *
 * Scope notes:
 * - No OAuth section (SPs don't have OAuth).
 * - No preflight / test-connection on Save. If validation is needed, users
 *   can save and rerun the workflow — mirrors the read-only behavior of the
 *   MCP + agent edit dialogs in this PR.
 * - Secret fields round-trip losslessly: if the initial value is
 *   `@appsetting('NAME')`, `extractServiceProviderAppSettings` treats that
 *   as already-referenced and leaves both the ref and the underlying
 *   app-setting value untouched.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useConnectorMetadata } from '@/hooks/useConnectionMutations';
import { useServiceProviderConnector } from '@/hooks/useServiceProviderConnector';
import { useEffectiveManifestWithFallback } from '@/hooks/useEffectiveManifest';
import {
  useEnvironmentSettings,
  useUpsertEnvironmentSettings,
} from '@/hooks/useEnvironmentSettings';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  buildConnectorManifest,
  getConnectionParameterSets,
  hasRealParameterSets,
} from '@/services/manifestConnectionService';
import {
  extractServiceProviderAppSettings,
  hasAppSettingReferences,
  resolveAppSettingReferences,
} from '@/utils/connections/serviceProviderAppSettings';
import type { FormValues } from '@/types/connectionParameters';
import type { ServiceProviderConnection } from '@/types/connections';
import { DiscardChangesDialog } from './editForm/DiscardChangesDialog';
import { ManifestConnectionForm } from './editForm/ManifestConnectionForm';
import { useManifestFormState } from './editForm/useManifestFormState';
import { useDiscardGuard } from './editForm/useDiscardGuard';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '800px',
    minWidth: '700px',
    minHeight: '480px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  footer: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
  errorBanner: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

export interface EditServiceProviderConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Key under `serviceProviderConnections` in connections.json. */
  connectionKey: string;
  /** The local connection record being edited. */
  connection: ServiceProviderConnection;
  /**
   * Called with the updated record. Routes through
   * `useConnectionsEditor.handleSaveLocalConnection(id, 'serviceProvider', record)`
   * so draft/prod mode is honored.
   */
  onSave: (recordKey: string, record: ServiceProviderConnection) => Promise<void>;
}

export const EditServiceProviderConnectionDialog = ({
  open,
  onClose,
  connectionKey,
  connection,
  onSave,
}: EditServiceProviderConnectionDialogProps) => {
  const styles = useStyles();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const selectedProject = useAppStore((state) => state.selectedProject);
  const selectedAppName = useAppStore((state) => state.selectedAppName);
  // Safe with empty project/app: the mutation short-circuits when there
  // are no additions, and we guard the .mutateAsync call below.
  const upsertAppSettings = useUpsertEnvironmentSettings(
    selectedProject ?? '',
    selectedAppName ?? '',
  );

  // Pull the app's environment settings so we can resolve any
  // `@appsetting('NAME')` references embedded in `parameterValues` back to
  // their real values before seeding the form. Without this, the user would
  // see literal reference strings in secret fields (e.g. the connection
  // string / account key) — surprising and un-editable in place. The query
  // is disabled when project/app aren't selected; the seed effect below
  // gracefully falls back to raw values in that case.
  const {
    data: envSettings,
    isLoading: isLoadingEnvSettings,
  } = useEnvironmentSettings(selectedProject ?? '', selectedAppName ?? '');
  const envSettingsMap = useMemo(() => {
    const map: Record<string, string | null | undefined> = {};
    (envSettings ?? []).forEach((s) => {
      map[s.name] = s.value;
    });
    return map;
  }, [envSettings]);

  // Fetch manifest sources. SPs are often "unfetchable" via the ARM-style
  // `useConnectorMetadata`, but `useServiceProviderConnector` pulls the
  // fuller runtime-side definition. `buildConnectorManifest` prefers the
  // latter and falls back to the former's trimmed embedded copy if needed.
  const connectorId = connection.serviceProvider.id;
  const { data: fetchedMetadata, isLoading: isLoadingMetadata } =
    useConnectorMetadata(connectorId);
  const { data: serviceProviderConnector, isLoading: isLoadingSpConnector } =
    useServiceProviderConnector(connectorId ?? null);

  const connectorManifest = useMemo(
    () =>
      buildConnectorManifest({
        connectorId,
        serviceProviderConnector,
        operationManifest: null,
      }),
    [connectorId, serviceProviderConnector],
  );
  const effectiveManifest = useEffectiveManifestWithFallback(
    connectorManifest,
    fetchedMetadata,
  );

  const parameterSets = useMemo(
    () => getConnectionParameterSets(effectiveManifest),
    [effectiveManifest],
  );
  const isRealMultiAuth = useMemo(
    () => hasRealParameterSets(effectiveManifest),
    [effectiveManifest],
  );

  const form = useManifestFormState();
  const { seed } = form;
  // Close/discard orchestration shared with the other ConnectionList edit
  // dialogs. Busy-guard mirrors the original: closes are blocked while the
  // secret-upsert mutation is in flight. `resetToOriginal` runs on discard.
  const discardGuard = useDiscardGuard({
    isDirty: form.hasChanges,
    isBusy: upsertAppSettings.isPending,
    onClose,
    onDiscard: form.resetToOriginal,
  });
  // Stable reference for the seed effect below; the guard object itself
  // re-identifies each render, but `keepEditing` is a stable callback.
  const { keepEditing: resetDiscardConfirm } = discardGuard;
  const [saveError, setSaveError] = useState<string | null>(null);

  // Detect whether we need env-settings before we can seed. If the record
  // has no `@appsetting()` refs, we can seed immediately from `connection`.
  // If it does but project/app aren't selected (query disabled), we still
  // seed with the raw refs rather than blocking forever — the user will see
  // the reference string and can wire secrets elsewhere.
  const rawParameterValues = useMemo(
    () => (connection.parameterValues ?? {}) as Record<string, unknown>,
    [connection.parameterValues],
  );
  const needsResolution = useMemo(
    () => hasAppSettingReferences(rawParameterValues),
    [rawParameterValues],
  );
  const canFetchEnvSettings = !!selectedProject && !!selectedAppName;
  const isWaitingForEnvSettings =
    needsResolution && canFetchEnvSettings && envSettings === undefined;

  // Seed once per open+record combination. Depending on the stable `seed`
  // callback (not the whole `form` object, which re-identifies every render)
  // is not enough on its own: `envSettings` also changes identity when
  // `useUpsertEnvironmentSettings` writes back via `setQueryData` on
  // success. Without a guard, a partial-success save (secrets upserted,
  // then `onSave` throws) would re-run this effect after the mutation,
  // wipe the user's local edits, and disable the Save button — leaving
  // them unable to retry. The ref tracks the connection key we've
  // successfully seeded so subsequent runs are no-ops until the dialog
  // closes or a different record is opened.
  const seededKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededKeyRef.current = null;
      return;
    }
    if (seededKeyRef.current === connectionKey) return;
    if (isWaitingForEnvSettings) return;
    const resolvedValues =
      needsResolution && envSettings !== undefined
        ? resolveAppSettingReferences(rawParameterValues, envSettingsMap)
        : rawParameterValues;
    seed({
      displayName: connection.displayName ?? connectionKey,
      // `connection.parameterValues` is `Record<string, unknown>` from the
      // connections.json schema. Widen to `FormValues` for the shared form
      // state — DynamicConnectionForm will only touch fields declared by the
      // parameter set, so any non-`FormFieldValue` leaves are inert.
      formValues: resolvedValues as FormValues,
      selectedParameterSetName: connection.parameterSetName,
    });
    seededKeyRef.current = connectionKey;
    resetDiscardConfirm();
    setSaveError(null);
  }, [
    open,
    connection,
    connectionKey,
    seed,
    isWaitingForEnvSettings,
    needsResolution,
    envSettings,
    envSettingsMap,
    rawParameterValues,
    resetDiscardConfirm,
  ]);

  const selectedParameterSet =
    form.selectedParameterSetName ?? parameterSets[0]?.name ?? '';
  const currentParameterSet = useMemo(
    () =>
      parameterSets.find((s) => s.name === selectedParameterSet) ?? parameterSets[0],
    [parameterSets, selectedParameterSet],
  );

  const isLoading =
    isLoadingMetadata ||
    isLoadingSpConnector ||
    (needsResolution && canFetchEnvSettings && isLoadingEnvSettings);

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    if (!form.displayName.trim()) {
      setSaveError('Connection name is required');
      return;
    }

    setIsSaving(true);
    // Tracks whether we've handed off to `onSave` (the JSON-save layer).
    // Once that call starts, the shared `mutateActiveConnections` handler
    // owns the success + error notifications for the write to
    // connections.json, so we must NOT double-notify from here — matches
    // how the agent + MCP dialogs behave (they don't notify at all).
    let onSaveInvoked = false;
    try {
      // Move `AppConfiguration`-sourced leaves (secrets etc.) into app
      // settings, replacing the inline value with an `@appsetting(NAME)`
      // reference. Untouched refs pass through unchanged.
      const { parameterValues: storedParamValues, appSettings } =
        extractServiceProviderAppSettings(
          connectionKey,
          form.formValues,
          currentParameterSet,
        );

      if (Object.keys(appSettings).length > 0) {
        if (!selectedProject || !selectedAppName) {
          throw new Error(
            'No project/app context available — cannot persist secret app settings.',
          );
        }
        await upsertAppSettings.mutateAsync(appSettings);
      }

      const updated: ServiceProviderConnection = {
        ...connection,
        displayName: form.displayName.trim(),
        parameterValues: storedParamValues,
        ...(form.selectedParameterSetName
          ? { parameterSetName: form.selectedParameterSetName }
          : {}),
      };

      onSaveInvoked = true;
      await onSave(connectionKey, updated);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update connection';
      setSaveError(message);
      // Only surface a notification for failures that happened BEFORE the
      // JSON save layer took over — validation, missing project/app, or
      // the secret-upsert step. Errors from `onSave` itself are already
      // notified by `mutateActiveConnections` in `useConnectionsEditor`.
      if (!onSaveInvoked) {
        addNotification({
          type: 'error',
          title: 'Failed to update connection',
          message,
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    form.displayName,
    form.formValues,
    form.selectedParameterSetName,
    connection,
    connectionKey,
    currentParameterSet,
    onSave,
    onClose,
    addNotification,
    upsertAppSettings,
    selectedProject,
    selectedAppName,
  ]);

  return (
    <>
      <Dialog open={open} onOpenChange={discardGuard.handleOpenChange}>
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={discardGuard.requestClose}
                />
              }
            >
              Edit service provider connection
            </DialogTitle>
            <DialogContent className={styles.content}>
              <ManifestConnectionForm
                displayName={form.displayName}
                onDisplayNameChange={form.setDisplayName}
                displayNamePlaceholder={connectionKey}
                displayNameTestId="edit-sp-connection-display-name"
                parameterSets={parameterSets}
                selectedParameterSet={selectedParameterSet}
                onParameterSetChange={form.setSelectedParameterSetName}
                values={form.formValues}
                onValuesChange={form.setFormValues}
                showAuthTypeSelector={isRealMultiAuth && parameterSets.length > 1}
                disabled={isSaving}
                isLoading={isLoading}
              />
              {saveError && (
                <div className={styles.errorBanner} role="alert">
                  {saveError}
                </div>
              )}
            </DialogContent>
            <DialogActions className={styles.footer}>
              <Button
                appearance="secondary"
                onClick={discardGuard.requestClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleSave}
                disabled={isSaving || !form.hasChanges}
                {...(isSaving ? { icon: <Spinner size="tiny" /> } : {})}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Discard-confirmation dialog — shared across every ConnectionList
          edit surface for consistent copy + affordances. */}
      <DiscardChangesDialog
        open={discardGuard.showDiscardConfirm}
        onKeepEditing={discardGuard.keepEditing}
        onDiscard={discardGuard.confirmDiscard}
      />
    </>
  );
};

EditServiceProviderConnectionDialog.displayName =
  'EditServiceProviderConnectionDialog';
